import { ViewType } from '@nirvana/shared/dtos/types/alerts/common';
import { MitreTags } from '../../../xdr/tacticsAndTechniques/types';
import { SmartViewGridConfig } from '../../../grid/genericGridSmartViews/types';
import { TimeRangeOption } from '../../../ui/gzDatetimeFilter/types';
import { translateFn } from '../../../utils/translator.helper';
import {GzSingleSelectWebComponent} from "../../../ui/gzSingleSelectDropdown";
import {ListItem} from "../../../ui/gzSingleSelectList/types";

export interface IncidentsGridWebComponentSchema {
  // all the data needed for rendering
  availableUsers: any;
}

export interface ExtraParametersSchema {
  incidentsData: IncidentsGridWebComponentSchema;
  externalFilters: Array<Object>;
}

export enum DropDownSelectEvents {
  viewEvents = 'viewEvents',
  prioritize = 'prioritize',
  assign = 'assign',
}

export enum ColumnKeys {
  NUMBER = 'number',
  CREATED = 'created',
  LAST_UPDATED = 'lastUpdated',
  STATUS = 'status',
  ASSIGNEE = 'assignee',
  PRIORITY = 'priority',
  SCORE = 'score',
  COMPANY = 'company.id',
  ENTITIES = 'entities',
  RESOURCES = 'resources',
  LAST_KILLCHAIN_PHASE = 'specifics.lastKillChainPhase',
  ACTORS = 'specifics.attribution.actors',
  ACTION_TAKEN = 'mainAction',
  CORRELATED = 'correlatedIncidents.number',
  SELECT = 'select',
  SEARCH_DATA = 'searchData',
  TYPE = 'type',
  PHASR_CATEGORIES = 'specifics.nodesCategories',
}

export enum INCIDENT_TYPE {
  XDR = 1,
  EDR = 2,
}

export enum ColumnIndexes {
  NUMBER,
  CREATED,
  LAST_UPDATED,
  STATUS,
  ASSIGNEE,
  PRIORITY,
  SCORE,
  COMPANY,
  ENTITIES,
  RESOURCES,
  LAST_KILLCHAIN_PHASE,
  ACTION_TAKEN,
  CORRELATED,
  SELECT,
  SEARCH_DATA,
  TYPE,
  PHASR_CATEGORIES,
  ACTORS,
}

export enum ScoreLevels {
  HIGH = 75,
  MEDIUM = 40,
  LOW = 10,
}

export const ENTITIES_TYPES = {
  ENDPOINT: 1,
  SERVER: 2,
  MOBILE_DEVICE: 3,
  PRINTER: 4,
  ROUTER: 5,
  IOT: 6,
  AWS: 30,
  AZURE: 31,
  GCP: 32,
  GOOGLE_WORKSPACE: 36,
  AD: 33,
  OFFICE_365: 34,
  AZURE_AD: 35,
  ATLASSIAN: 37,
  VIRTUAL_SERVER_AWS: 40,
  CONTAINER_AWS: 41,
  SERVICE_AWS: 42,
  DATABASE_AWS: 43,
  STORAGE_AWS: 44,
  END_USER_COMPUTING_AWS: 45,
  VIRTUAL_SERVER_AZURE: 50,
  CONTAINER_AZURE: 51,
  SERVICE_AZURE: 52,
  DATABASE_AZURE: 53,
  STORAGE_AZURE: 54,
  END_USER_COMPUTING_AZURE: 55,
  VIRTUAL_SERVER_GCP: 60,
  CONTAINER_GCP: 61,
  SERVICE_GCP: 62,
  DATABASE_GCP: 63,
  STORAGE_GCP: 64,
  END_USER_COMPUTING_GCP: 65,
  PRODUCT_ATLASSIAN_BITBUCKET: 70,
  PRODUCT_ATLASSIAN_JIRA: 71,
  PRODUCT_ATLASSIAN_CONFLUENCE: 72,
  BITBUCKET_PROJECT: 73,
  CONFLUENCE_SPACE: 74,
  USER_GENERIC: 20,
  USER_AWS: 21,
  USER_GCP: 23,
  USER_AD: 24,
  USER_AZURE_AD: 25,
  USER_ATLASSIAN: 26,
  ATTACKER: 101,
  EMAIL: 102,
  IP_SERVER: 103,
  DOMAIN: 104,
  DNS: 105,
  DGA: 106,
  CLOUD_STORAGE: 107,
  TOR_NODE: 108,
  EXTERNAL_DRIVE: 109,
};

export const ENTITIES_TYPES_ORDER = [
  ENTITIES_TYPES.ENDPOINT,
  ENTITIES_TYPES.SERVER,
  ENTITIES_TYPES.VIRTUAL_SERVER_AWS,
  ENTITIES_TYPES.VIRTUAL_SERVER_AZURE,
  ENTITIES_TYPES.VIRTUAL_SERVER_GCP,
  ENTITIES_TYPES.CONTAINER_AWS,
  ENTITIES_TYPES.CONTAINER_AZURE,
  ENTITIES_TYPES.CONTAINER_GCP,
  ENTITIES_TYPES.USER_GENERIC,
  ENTITIES_TYPES.USER_AWS,
  ENTITIES_TYPES.USER_GCP,
  ENTITIES_TYPES.USER_AD,
  ENTITIES_TYPES.USER_AZURE_AD,
  ENTITIES_TYPES.USER_ATLASSIAN,
  ENTITIES_TYPES.END_USER_COMPUTING_AWS,
  ENTITIES_TYPES.END_USER_COMPUTING_AZURE,
  ENTITIES_TYPES.END_USER_COMPUTING_GCP,
  ENTITIES_TYPES.PRODUCT_ATLASSIAN_BITBUCKET,
  ENTITIES_TYPES.PRODUCT_ATLASSIAN_JIRA,
  ENTITIES_TYPES.PRODUCT_ATLASSIAN_CONFLUENCE,
  ENTITIES_TYPES.BITBUCKET_PROJECT,
  ENTITIES_TYPES.CONFLUENCE_SPACE,
  ENTITIES_TYPES.IP_SERVER,
  ENTITIES_TYPES.DOMAIN,
  ENTITIES_TYPES.CLOUD_STORAGE,
  ENTITIES_TYPES.STORAGE_AWS,
  ENTITIES_TYPES.STORAGE_AZURE,
  ENTITIES_TYPES.STORAGE_GCP,
  ENTITIES_TYPES.DATABASE_AWS,
  ENTITIES_TYPES.DATABASE_AZURE,
  ENTITIES_TYPES.DATABASE_GCP,
  ENTITIES_TYPES.MOBILE_DEVICE,
  ENTITIES_TYPES.PRINTER,
  ENTITIES_TYPES.ROUTER,
  ENTITIES_TYPES.IOT,
  ENTITIES_TYPES.AWS,
  ENTITIES_TYPES.AZURE,
  ENTITIES_TYPES.GCP,
  ENTITIES_TYPES.GOOGLE_WORKSPACE,
  ENTITIES_TYPES.AD,
  ENTITIES_TYPES.OFFICE_365,
  ENTITIES_TYPES.AZURE_AD,
  ENTITIES_TYPES.ATLASSIAN,
  ENTITIES_TYPES.SERVICE_AWS,
  ENTITIES_TYPES.SERVICE_AZURE,
  ENTITIES_TYPES.SERVICE_GCP,
  ENTITIES_TYPES.ATTACKER,
  ENTITIES_TYPES.EMAIL,
  ENTITIES_TYPES.DNS,
  ENTITIES_TYPES.DGA,
  ENTITIES_TYPES.TOR_NODE,
  ENTITIES_TYPES.EXTERNAL_DRIVE,
];

export const ENTITIES_CLASS = new Map<number, string>([
  [ENTITIES_TYPES.ENDPOINT, 'icon-endpoint'],
  [ENTITIES_TYPES.SERVER, 'icon-servers'],
  [ENTITIES_TYPES.MOBILE_DEVICE, 'icon-mobile_device'],
  [ENTITIES_TYPES.PRINTER, 'icon-printer'],
  [ENTITIES_TYPES.ROUTER, 'icon-router'],
  [ENTITIES_TYPES.IOT, 'icon-IOT'],
  [ENTITIES_TYPES.AWS, 'icon-Integration-AWS'],
  [ENTITIES_TYPES.AZURE, 'icon-Integration-Azure-AD'],
  [ENTITIES_TYPES.GCP, 'icon-Integration-GCP'],
  [ENTITIES_TYPES.GOOGLE_WORKSPACE, 'icon-entity_Google_Workspace'],
  [ENTITIES_TYPES.AD, 'icon-Integration-AD'],
  [ENTITIES_TYPES.OFFICE_365, 'icon-User-Office365'],
  [ENTITIES_TYPES.AZURE_AD, 'icon-Integration-Azure-AD'],
  [ENTITIES_TYPES.ATLASSIAN, 'icon-atlassian'],
  [ENTITIES_TYPES.VIRTUAL_SERVER_AWS, 'icon-Virtual-Server-AWS'],
  [ENTITIES_TYPES.CONTAINER_AWS, 'icon-Container-AWS'],
  [ENTITIES_TYPES.SERVICE_AWS, 'icon-Server-AWS'],
  [ENTITIES_TYPES.DATABASE_AWS, 'icon-Database-AWS'],
  [ENTITIES_TYPES.STORAGE_AWS, 'icon-Storage-AWS'],
  [ENTITIES_TYPES.END_USER_COMPUTING_AWS, 'icon-User-AWS'],
  [ENTITIES_TYPES.VIRTUAL_SERVER_AZURE, 'icon-Virtual-Server-Azure'],
  [ENTITIES_TYPES.CONTAINER_AZURE, 'icon-Container-Azure'],
  [ENTITIES_TYPES.SERVICE_AZURE, 'icon-Server-Azure'],
  [ENTITIES_TYPES.DATABASE_AZURE, 'icon-Database-Azure'],
  [ENTITIES_TYPES.STORAGE_AZURE, 'icon-Storage-Azure'],
  [ENTITIES_TYPES.END_USER_COMPUTING_AZURE, 'icon-User-Azure'],
  [ENTITIES_TYPES.VIRTUAL_SERVER_GCP, 'icon-Virtual-Server-GCP'],
  [ENTITIES_TYPES.CONTAINER_GCP, 'icon-Container-GCP'],
  [ENTITIES_TYPES.SERVICE_GCP, 'icon-Server-GCP'],
  [ENTITIES_TYPES.DATABASE_GCP, 'icon-Database-GCP'],
  [ENTITIES_TYPES.STORAGE_GCP, 'icon-Storage-GCP'],
  [ENTITIES_TYPES.END_USER_COMPUTING_GCP, 'icon-User-GCP'],
  [ENTITIES_TYPES.PRODUCT_ATLASSIAN_BITBUCKET, 'icon-product_atlassian_bitbucket'],
  [ENTITIES_TYPES.PRODUCT_ATLASSIAN_JIRA, 'icon-product_atlassian_jira'],
  [ENTITIES_TYPES.PRODUCT_ATLASSIAN_CONFLUENCE, 'icon-product_atlassian_confluence'],
  [ENTITIES_TYPES.BITBUCKET_PROJECT, 'icon-bitbucket_project'],
  [ENTITIES_TYPES.CONFLUENCE_SPACE, 'icon-confluence_space'],
  [ENTITIES_TYPES.USER_GENERIC, 'icon-User-Generic'],
  [ENTITIES_TYPES.USER_AWS, 'icon-User-AWS'],
  [ENTITIES_TYPES.USER_GCP, 'icon-User-GCP'],
  [ENTITIES_TYPES.USER_AD, 'icon-User-AD'],
  [ENTITIES_TYPES.USER_AZURE_AD, 'icon-User-Azure'],
  [ENTITIES_TYPES.USER_ATLASSIAN, 'icon-user_atlassian'],
  [ENTITIES_TYPES.ATTACKER, 'icon-attacker'],
  [ENTITIES_TYPES.EMAIL, 'icon-mail'],
  [ENTITIES_TYPES.IP_SERVER, 'icon-domain'],
  [ENTITIES_TYPES.DOMAIN, 'icon-domain'],
  [ENTITIES_TYPES.DNS, 'icon-domain'],
  [ENTITIES_TYPES.DGA, 'icon-domain'],
  [ENTITIES_TYPES.CLOUD_STORAGE, 'icon-cloud_storage'],
  [ENTITIES_TYPES.TOR_NODE, 'icon-TOR-node'],
  [ENTITIES_TYPES.EXTERNAL_DRIVE, 'icon-external_drive'],
]);

export const RESOURCES_TYPES = {
  GENERIC: 10,
  APPLICATION: 11,
  KEY_VAULT: 12,
  ROLE: 13,
  POLICY: 14,
  SHARING_LINK: 15,
  FILE: 16,
  EMAIL: 17,
  FLOW: 18,
  URL: 19,
  SSH_KEY: 20,
  LAUNCH_TEMPLATE: 21,
  SERVICE_PRINCIPAL: 22,
  USER_GROUP: 23,
  AUTOMATION_ACCOUNT: 24,
  AUTOMATION_ACCOUNT_HOOK: 25,
  API: 26,
  CERTIFICATE_AUTHORITY: 27,
  BUCKET: 28,
  BITBUCKET_REPOSITORY: 29,
  JIRA_PROJECT: 30,
  CONFLUENCE_PAGE: 31,
};

export const RESOURCES_TYPES_ORDER = [
  RESOURCES_TYPES.FILE,
  RESOURCES_TYPES.EMAIL,
  RESOURCES_TYPES.URL,
  RESOURCES_TYPES.SHARING_LINK,
  RESOURCES_TYPES.SSH_KEY,
  RESOURCES_TYPES.ROLE,
  RESOURCES_TYPES.POLICY,
  RESOURCES_TYPES.FLOW,
  RESOURCES_TYPES.APPLICATION,
  RESOURCES_TYPES.KEY_VAULT,
  RESOURCES_TYPES.LAUNCH_TEMPLATE,
  RESOURCES_TYPES.USER_GROUP,
  RESOURCES_TYPES.SERVICE_PRINCIPAL,
  RESOURCES_TYPES.AUTOMATION_ACCOUNT,
  RESOURCES_TYPES.AUTOMATION_ACCOUNT_HOOK,
  RESOURCES_TYPES.API,
  RESOURCES_TYPES.CERTIFICATE_AUTHORITY,
  RESOURCES_TYPES.BUCKET,
  RESOURCES_TYPES.BITBUCKET_REPOSITORY,
  RESOURCES_TYPES.JIRA_PROJECT,
  RESOURCES_TYPES.CONFLUENCE_PAGE,
  RESOURCES_TYPES.GENERIC,
];

export const RESOURCES_CLASS = new Map<number, string>([
  [RESOURCES_TYPES.GENERIC, 'icon-Generic-resource'],
  [RESOURCES_TYPES.APPLICATION, 'icon-Application'],
  [RESOURCES_TYPES.KEY_VAULT, 'icon-KeyVault'],
  [RESOURCES_TYPES.ROLE, 'icon-user'],
  [RESOURCES_TYPES.POLICY, 'icon-menu_policy'],
  [RESOURCES_TYPES.SHARING_LINK, 'icon-Share-link-or-file'],
  [RESOURCES_TYPES.FILE, 'icon-file'],
  [RESOURCES_TYPES.EMAIL, 'icon-mail'],
  [RESOURCES_TYPES.FLOW, 'icon-Flow'],
  [RESOURCES_TYPES.URL, 'icon-URL'],
  [RESOURCES_TYPES.SSH_KEY, 'icon-SSH'],
  [RESOURCES_TYPES.LAUNCH_TEMPLATE, 'icon-Launch-template'],
  [RESOURCES_TYPES.SERVICE_PRINCIPAL, 'icon-resource_account_principal'],
  [RESOURCES_TYPES.USER_GROUP, 'icon-resource_user_groups'],
  [RESOURCES_TYPES.AUTOMATION_ACCOUNT, 'icon-resource_automation_account'],
  [RESOURCES_TYPES.AUTOMATION_ACCOUNT_HOOK, 'icon-resource_automation_account_hook'],
  [RESOURCES_TYPES.API, 'icon-resource_API'],
  [RESOURCES_TYPES.CERTIFICATE_AUTHORITY, 'icon-resource_certificate_authority'],
  [RESOURCES_TYPES.BUCKET, 'icon-resource_bucket'],
  [RESOURCES_TYPES.BITBUCKET_REPOSITORY, 'icon-bitbucket_repository'],
  [RESOURCES_TYPES.JIRA_PROJECT, 'icon-jira_project'],
  [RESOURCES_TYPES.CONFLUENCE_PAGE, 'icon-confluence_page'],
]);

export const KILL_CHAIN_PHASES = {
  INITIAL_ACCESS: 1,
  EXECUTION: 2,
  PERSISTENCE: 3,
  PRIVILEGE_ESCALATION: 4,
  DEFENCE_EVASION: 5,
  CREDENTIAL_ACCESS: 6,
  DISCOVERY: 7,
  LATERAL_MOVEMENT: 8,
  COLLECTION: 9,
  COMMAND_AND_CONTROL: 10,
  EXFILTRATION: 11,
  IMPACT: 12,
};

export interface CategoriesList {
  icon?: string;
  text: string;
  type: number;
  isOpen?: boolean;
  items: Array<ItemData>;
}

export interface ItemData {
  name: string;
  uid: number;
  incidents: Array<any>;

  index?: number;
}

export enum TriggerType {
  PROCESS = 1,
  FILE = 2,
  DOMAIN = 3,
}

export interface TriggerSpecifics {
  processName?: string;
  fileName?: string;
  pid?: string;
  url?: string;
}

export interface Trigger {
  type: number;
  specifics: TriggerSpecifics;
}

export interface IncidentSpecifics {
  trigger: Trigger;
  alerts: any;
  killChainPhases?: any;
  endpoint?: any;
  counters?: any;
  attribution?: IncidentAttribution;
}

export interface IncidentAttribution {
  actors: Array<IncidentActor>;
}

export interface IncidentActor {
  aliases: Array<string>;
  country: Array<string>;
  confidence: number;
  cve: Array<string>;
  description: string;
  eCrimes: Array<string>;
  id: string;
  intellizoneId: string;
  iocs: Array<string>;
  mitreTechniques: Array<string>;
  motivation: Array<string>;
  name: string;
  sponsor: Array<string>;
  targetCountries: Array<string>;
  targetsSector: boolean;
  targetsCountry: boolean;
  firstDetected?: Date;
}

export interface ExtraInformation {
  assigneeName?: string;
  actionTakenValue?: string;
}

export interface IncidentData {
  _id?: string;
  number: number;
  created: string;
  lastUpdated: string;
  company: Object;
  score: number;
  status: string;
  assignee: Object;
  priority: string;
  specifics: IncidentSpecifics;
  mitreTags: Array<MitreTags>;
  extra?: ExtraInformation;
  mainAction: number;
  resources?: Array<any>;
  entities: Array<any>;
  alerts: Array<any>;
}

export function convertGenericConfigToGridConfig(viewConfig): SmartViewGridConfig {
  // the grid only understands 2 view types , default and custom. These are used to enable/disabled
  // action buttons (Save As, Save, Discard Changes)
  // so we need to convert our current view types to the types that the grid can understand
  let type = ViewType.CUSTOM;
  if (viewConfig.isReadOnly) {
    type = ViewType.DEFAULT;
  }
  return {
    id: viewConfig._id,
    type: type,
    label: viewConfig.label,
    isFavorite: viewConfig.isFavorite,
    isRecent: viewConfig.isRecent,
    isReadOnly: viewConfig.isReadOnly,
    filters:
      typeof viewConfig.specifics.filters === 'string'
        ? JSON.parse(viewConfig.specifics.filters)
        : viewConfig.specifics.filters,
    displayedColumns: viewConfig.specifics.displayedColumns,
    compactView: viewConfig.specifics.compactView,
    columnConfigs: viewConfig.specifics.columnConfigs,
    pageSize: viewConfig.specifics.pageSize,
    isDefault: !!viewConfig.specifics.isDefault,
  };
}

export enum ACTION_TAKEN {
  REPORTED = 1,
  BLOCKED = 2,
  PARTIALLY_BLOCKED = 3,
}

export enum IncidentEvents {
  SHOW_INCIDENT = 'showIncident',
}

export enum STATUS {
  OPEN = 1,
  INVESTIGATING = 2,
  CLOSED = 3,
  FALSE_POSITIVE = 4,
}

export enum PRIORITY {
  UNASSIGNED = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  CRITICAL = 4,
}

export enum COMPLEX_SEARCH_FILTERS {
  ALERT_NAME = 'searchFields.values.alerts',
  TECHNIQUE = 'mitre.name',
  TECHNIQUE_ID = 'mitre.id',
  SUBTECHNIQUE = 'mitre.subtechniques.name',
  SUBTECHNIQUE_ID = 'mitre.subtechniques.id',
  IP = 'searchFields.values.ip',
  MD5 = 'searchFields.values.md5',
  SHA256 = 'searchFields.values.sha256',
  NODE_NAME = 'searchFields.values.name',
  USER_NAME = 'searchFields.values.username',
  FILE_NAME = 'searchFields.values.fileName',
  FILE_PATH = 'searchFields.values.filePath',
  URL = 'searchFields.values.url',
  MAC = 'searchFields.values.mac',
  EMAIL_SUBJECT = 'searchFieldsAlerts.values.emailSubjects',
  EMAIL_ADDRESS = 'searchFieldsAlerts.values.emailAddresses',
  PROCESS_NAME = 'searchFields.values.processName',
  PROCESS_PID = 'searchFields.values.pid',
  PROCESS_PATH = 'searchFields.values.processPath',
  REGISTRY_KEY = 'searchFields.values.key',
  DETECTION_RULES_ID = 'searchFields.values.userDetectionRulesIds',
}

export type EdrStorageTimeRange = 'LAST_90_DAYS' | 'LAST_180_DAYS' | 'LAST_365_DAYS';
type EdrStorageTimeRanges = Record<EdrStorageTimeRange, number>;

const EDR_STORAGE_RELATIVE_TIME_RANGES: EdrStorageTimeRanges = {
  LAST_90_DAYS: 90,
  LAST_180_DAYS: 180,
  LAST_365_DAYS: 365,
};

export const computeTimeOptionsFromEdrStorageTimeRange = (
  edrStorageTimeRange: EdrStorageTimeRange
): TimeRangeOption[] => {
  const timeOptions = [
    {
      label: translateFn('Last 90 days'),
      value: EDR_STORAGE_RELATIVE_TIME_RANGES.LAST_90_DAYS,
    },
  ];

  if (edrStorageTimeRange === 'LAST_180_DAYS' || edrStorageTimeRange === 'LAST_365_DAYS') {
    timeOptions.push({
      label: translateFn('Last 180 days'),
      value: EDR_STORAGE_RELATIVE_TIME_RANGES.LAST_180_DAYS,
    });
  }

  if (edrStorageTimeRange === 'LAST_365_DAYS') {
    timeOptions.push({
      label: translateFn('Last 365 days'),
      value: EDR_STORAGE_RELATIVE_TIME_RANGES.LAST_365_DAYS,
    });
  }

  return timeOptions;
};
export const ACTORS_CLASS = new Map<any, any>([]);

export interface DialogActionTexts {
  headline: string,
  changeBulk: string,
  correlatedCheckbox: string,
  successToast: string,
  errorToast: string,
}

export interface AdditionalElementInfo {
  label: string,
  value: string | number,
  options: ListItem[],
  hasSearch?: boolean,
}

export enum BulkActionFields {
  STATUS = 'status',
  PRIORITY = 'priority',
  ASSIGNED_USER = 'assignedUser',
}
